import {concatMap, from, last, Observable, takeWhile} from 'rxjs';

import {inject} from '@angular/core';
import {ActivatedRouteSnapshot, CanActivate, CanActivateFn, RouterStateSnapshot, UrlTree} from '@angular/router';

interface AsyncGuard extends CanActivate {
	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree>;
}

// Accept both class type guards and function guards
type GuardItem = (new () => AsyncGuard) | CanActivateFn;

function isGuardClass(item: any): item is new () => AsyncGuard {
	return typeof item === 'function' && item.prototype && item.prototype.canActivate;
}

/**
 * Creates a combined guard function that executes multiple guards in order
 * @param guards Array of guard classes or guard functions to be executed in order
 * @returns A CanActivateFn that combines all guards
 */
export function orderedAsyncGuards(guards: GuardItem[]): CanActivateFn {
	return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
		// Process each guard
		const guardFunctions = guards.map(guard => {
			if (isGuardClass(guard)) {
				// It's a class, instantiate it with DI
				const instance = inject(guard);
				return (r: ActivatedRouteSnapshot, s: RouterStateSnapshot) => instance.canActivate(r, s);
			} else {
				// It's already a function
				return guard;
			}
		});

		// Convert array of guards into an observable
		return from(guardFunctions).pipe(
			// For each guard, execute its function
			concatMap(guardFn => guardFn(route, state) as Observable<boolean>),
			// Continue only if guards return true
			takeWhile(value => value === true, true),
			// Return the last guard's result
			last(),
		);
	};
}
